import { useQuery } from "@apollo/client";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import {
  DocumentUploadSection,
  SubmissionDocument,
} from "components/Submission/DocumentUploadSection";
import { SubmissionPaymentDisplay } from "components/Submission/SubmissionPaymentDisplay";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import {
  DocumentType,
  GetSubmissionSubmitAndFileQuery,
  SubmissionDocumentType,
  SubmissionStatus,
  SubmissionStepType,
} from "generated-graphql/graphql";
import { useStepperNavigation } from "hooks/useStepperNavigation";
import { useTenant } from "hooks/useTenant";
import { useWasteStepper } from "hooks/useWasteStepper";
import { formatDateLikeForDisplay } from "util/dates";

import { MarkFilingCompleteAction } from "../MarkFilingCompleteAction";
import { GET_SUBMISSION_SUBMIT_AND_FILE_QUERY } from "../queries";
import { StepHeader } from "../StepHeader";
import { StepSubHeader } from "../StepSubHeader";

type StepData = NonNullable<GetSubmissionSubmitAndFileQuery["submission"]>;
type DocumentRow = NonNullable<StepData["documents"]>[number];

function toDisplayDocument(row: DocumentRow): SubmissionDocument {
  return {
    id: row.document.id,
    submissionDocumentId: row.id,
    name: row.document.title || "Untitled",
    documentType: row.document.documentType ?? DocumentType.Other,
    submissionDocumentType: row.type,
    status: row.status ?? undefined,
    url: row.document.storageLink ?? undefined,
    fileExtension: row.document.fileExtension,
  };
}

/**
 * Submit & File step (document forms): the filing details and the signed
 * submission. There is deliberately no forward transition here — moving into
 * review/processing belongs to the review & sign flow — so the step's actions
 * are the staff-only "Mark as filing complete" once the submission reaches
 * processing, and a filed banner once complete.
 */
export function SubmitAndFileStep({ submissionId }: { submissionId: string; facilityId: string }) {
  const { tenantId } = useTenant();
  const { submission, canEdit } = useWasteStepper();
  const { goToPreviousStep } = useStepperNavigation();

  const { data } = useQuery(GET_SUBMISSION_SUBMIT_AND_FILE_QUERY, {
    variables: { id: submissionId },
    fetchPolicy: "cache-and-network",
  });
  const stepData = data?.submission;
  const documents = stepData?.documents ?? [];
  const signedRows = documents.filter(
    (row) => row.type === SubmissionDocumentType.SignedSubmission
  );
  const hasSubmissionDocument = documents.some(
    (row) => row.type === SubmissionDocumentType.Submission
  );

  const status = submission?.status;
  const isFiled = status === SubmissionStatus.FilingComplete;
  const isProcessing = status === SubmissionStatus.ProcessingSubmission;

  return (
    <Stack spacing={3}>
      <StepHeader title="Submit & File" description="Filing details and the signed submission." />
      {isFiled && <Alert severity="success">This submission has been filed.</Alert>}
      {isProcessing && (
        <Alert severity="info">
          Submitted for processing. Mark the filing complete once it has been submitted to the
          agency.
        </Alert>
      )}
      {!isFiled && !isProcessing && hasSubmissionDocument && (
        <Alert severity="info">The submission document is attached and ready to file.</Alert>
      )}
      <Stack spacing={1}>
        <StepSubHeader title="Filing details" />
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
          <Box sx={{ flex: 1, minWidth: 280 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Recipient
            </Typography>
            <Typography variant="body1">
              {stepData?.recipient ? prettyPrintEnumValue(stepData.recipient) : "—"}
            </Typography>
          </Box>
          <Box sx={{ width: 220 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Submission method
            </Typography>
            <Typography variant="body1">
              {stepData?.submissionMethod ? prettyPrintEnumValue(stepData.submissionMethod) : "—"}
            </Typography>
          </Box>
          <Box sx={{ width: 220 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Due date
            </Typography>
            <Typography variant="body1">
              {stepData?.dueDate ? formatDateLikeForDisplay(stepData.dueDate) : "—"}
            </Typography>
          </Box>
        </Box>
        <SubmissionPaymentDisplay submissionPayments={stepData?.payments ?? []} />
      </Stack>
      <Stack spacing={1}>
        <StepSubHeader title="Signed submission" />
        {signedRows.length > 0 ? (
          <DocumentUploadSection
            documents={signedRows.map(toDisplayDocument)}
            onDeleteDocument={() => {}}
            canDeleteDocument={() => false}
            tenantId={tenantId ?? undefined}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            The signed submission will appear here after review and signing.
          </Typography>
        )}
      </Stack>
      <MarkFilingCompleteAction />
      {canEdit && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
          <Button variant="text" onClick={() => goToPreviousStep(SubmissionStepType.SubmitAndFile)}>
            Back
          </Button>
        </Box>
      )}
    </Stack>
  );
}
